"use client";

import React, { useEffect, useRef, useState } from "react";
import { ClipboardCheck, PackageOpen, SearchCheck, Sparkles, ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  title: "Coordination",
  description: "We establish a centralized command structure, aligning global resources and stakeholder expectations to create a robust project roadmap.",
  icon: ClipboardCheck
}, {
  title: "Logistics",
  description: "Our experts manage the complex movement of assets and personnel across international borders, ensuring every component is in place for launch.",
  icon: PackageOpen
}, {
  title: "Execution",
  description: "We deploy our specialized methodology to oversee operations in real-time, maintaining precision and agility in dynamic global environments.",
  icon: Sparkles
}, {
  title: "Optimization",
  description: "A rigorous post-project analysis ensures all KPIs were exceeded, refining our global standards for your next large-scale operation.",
  icon: SearchCheck
}];
export default function ThePerfectProtocol() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height;
      const currentScrollPosition = windowHeight / 2 - rect.top;
      let percentage = currentScrollPosition / totalScrollableDistance * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      setProgress(percentage);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="222853" id="the-nova-methodology" ref={sectionRef} className="bg-background text-foreground relative min-h-screen">
      {}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border py-4 px-6 shadow-sm">
        <div className="max-w-4xl mx-auto flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <h2 className="text-lg md:text-xl font-default font-bold tracking-tight text-foreground uppercase">
              The Nova Methodology
            </h2>
            <span className="text-sm font-mono text-primary font-bold">
              PHASE {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-1.5 bg-muted" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-24 md:py-32">
        <div className="text-center mb-24">
          <h3 className="text-3xl md:text-6xl font-default font-bold text-foreground mb-6 tracking-tighter">
            Global Precision, <br className="hidden md:block" /> Seamless Execution.
          </h3>
          <p className="text-secondary text-lg max-w-2xl mx-auto font-default">
            Our consultancy is built on the Orbital Perspective. Discover the four-stage 
            framework that guarantees operational excellence for multi-national projects.
          </p>
        </div>

        {}
        <div className="relative border-l-2 border-primary/20 ml-4 md:ml-12 space-y-32 pb-24">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <div key={i} data-index={i} className="relative pl-8 md:pl-20 min-h-[30vh] flex flex-col justify-center group">
                {}
                <div className="absolute -left-[11px] top-1/2 -translate-y-1/2 size-5 rounded-full bg-background border-4 border-primary shadow-[0_0_15px_rgba(0,186,255,0.5)] transition-transform duration-500 group-hover:scale-125" />
                
                {}
                <span className="absolute -left-8 md:-left-12 top-1/2 -translate-y-1/2 text-[8rem] md:text-[14rem] font-mono font-bold text-primary/5 select-none leading-none pointer-events-none transition-colors duration-500 group-hover:text-primary/10">
                  0{i + 1}
                </span>

                <Card className="relative z-10 border-none shadow-none bg-transparent overflow-visible">
                  <CardHeader className="px-0 pt-0 pb-4">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="p-3 rounded-md bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                        <Icon className="size-6" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] font-mono text-primary font-bold uppercase tracking-widest mb-1">Global Standard Achieved</span>
                        <CardTitle className="text-2xl md:text-4xl font-default font-bold text-foreground">
                          {step.title}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0">
                    <p className="text-secondary text-lg leading-relaxed max-w-xl font-default">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-12 text-center relative z-20">
          <Button asChild size="lg" className="rounded-none px-10 bg-primary hover:bg-primary/90 text-primary-foreground font-default font-bold uppercase tracking-wider transition-all">
            <Link to="#">
              Deploy Methodology
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}